import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faChartLine, faFilter, faDownload, faCalendarAlt, faExpand, faTimes, faInfoCircle, faBrain, faLightbulb, faExclamationTriangle, faArrowTrendUp, faHeart, faThumbsUp, faSmile, faFrown, faMeh, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faTwitter, faLinkedin, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { useAudience } from '../../context/AudienceContext';
import { useAuth } from '../../context/AuthContext';
import { audienceAPI, creditsAPI, analysisJobsAPI, businessAuthAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BubbleController,
  BubbleDataPoint
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BubbleController
);

const AudiencePage: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { state, fetchAgeDemographics, fetchGenderDemographics, fetchGrowthData, fetchEngagementData, fetchInterestsData, fetchSentimentData, fetchAIAnalysis, fetchAllAudienceData } = useAudience();
  const { business, refreshBusinessProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [modalChart, setModalChart] = useState<string | null>(null);
  
  // Check analysis status from business model
  const analysisStatus = business?.analysisStatus?.['social-media-audience']?.status || 'noanalyze';
  const isAnalysisPending = analysisStatus === 'pending';
  const isAnalysisCompleted = analysisStatus === 'completed';
  const isAnalysisFailed = analysisStatus === 'failed';
  const hasAnyAnalysis = isAnalysisCompleted;

  // Analysis start flow states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Social media username setup states
  const [platforms, setPlatforms] = useState<Record<string, { username: string; loading: boolean; profile: any; error: string | null; verified: boolean; lastVerifiedUsername?: string | null }>>({
    facebook: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    instagram: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    twitter: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    tiktok: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    linkedin: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
  });
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Chart refs
  const sentimentChartRef = useRef<HTMLCanvasElement | null>(null);
  const ageChartRef = useRef<HTMLCanvasElement | null>(null);
  const genderChartRef = useRef<HTMLCanvasElement | null>(null);
  const interestsChartRef = useRef<HTMLCanvasElement | null>(null);
  const activityChartRef = useRef<HTMLCanvasElement | null>(null);
  const growthChartRef = useRef<HTMLCanvasElement | null>(null);

  // Modal chart refs
  const modalSentimentChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalAgeChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalGenderChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalInterestsChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalActivityChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalGrowthChartRef = useRef<HTMLCanvasElement | null>(null);

  const chartInstancesRef = useRef<{ [key: string]: ChartJS | null }>({});

  // Helper function to convert percentage strings to numbers
  const convertPercentageToNumber = (data: any) => {
    if (!data || !data.datasets) return data;
    
    return {
      ...data,
      datasets: data.datasets.map((dataset: any) => ({
        ...dataset,
        data: (Array.isArray(dataset.data) ? dataset.data : typeof dataset.data === 'number' ? [dataset.data] : []).map((value: any) => {
          if (typeof value === 'string' && value.includes('%')) {
            return parseFloat(value.replace('%', ''));
          }
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        })
      }))
    };
  };

  const normalizeInterestsData = (data: any) => {
    if (!data) return data;

    const labels = Array.isArray(data.labels) ? data.labels : [];
    const dataMap = new Map<string, number>();

    if (Array.isArray(data.datasets)) {
      data.datasets.forEach((dataset: any) => {
        const label = dataset?.label;
        const rawValue = dataset?.data;
        let value = 0;

        if (Array.isArray(rawValue)) {
          value = rawValue[0] ?? 0;
        } else if (typeof rawValue === 'number') {
          value = rawValue;
        } else if (typeof rawValue === 'string') {
          value = parseFloat(rawValue) || 0;
        }

        if (label) {
          dataMap.set(label, value);
        }
      });
    }

    const colorPalette = [
      'rgba(99, 102, 241, 0.7)',
      'rgba(34, 197, 94, 0.7)',
      'rgba(234, 179, 8, 0.7)',
      'rgba(239, 68, 68, 0.7)',
      'rgba(59, 130, 246, 0.7)',
      'rgba(236, 72, 153, 0.7)',
      'rgba(16, 185, 129, 0.7)',
      'rgba(245, 158, 11, 0.7)',
    ];

    const values = labels.map((label: string) => dataMap.get(label) ?? 0);

    return {
      labels,
      datasets: [
        {
          label: t('socialMedia.audience.charts.interests'),
          data: values,
          backgroundColor: labels.map((_: string, index: number) => colorPalette[index % colorPalette.length]),
          borderRadius: 8,
        },
      ],
    };
  };

  // Helper function to convert string numbers to numbers
  const convertStringToNumber = (data: any) => {
    if (!data || !data.datasets) return data;
    
    return {
      ...data,
      datasets: data.datasets.map((dataset: any) => ({
        ...dataset,
        data: (Array.isArray(dataset.data) ? dataset.data : typeof dataset.data === 'number' ? [dataset.data] : []).map((value: any) => {
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        })
      }))
    };
  };

  // Calculate dynamic metrics from API data
  const calculateMetrics = () => {
    let totalAudience = 0;
    let activeUsers = 0;
    let avgSentiment = 0;
    let engagementRate = 0;

    // Calculate total audience from growth data
    if (state.growth && state.growth.datasets && state.growth.datasets.length > 0) {
      const latestGrowthData = state.growth.datasets[0].data;
      if (latestGrowthData && latestGrowthData.length > 0) {
        const lastValue = latestGrowthData[latestGrowthData.length - 1];
        totalAudience = typeof lastValue === 'number' ? lastValue : 0;
      }
    }

    // Calculate active users from growth data (second dataset)
    if (state.growth && state.growth.datasets && state.growth.datasets.length > 1) {
      const activeUsersData = state.growth.datasets[1].data;
      if (activeUsersData && activeUsersData.length > 0) {
        const lastValue = activeUsersData[activeUsersData.length - 1];
        activeUsers = typeof lastValue === 'number' ? lastValue : 0;
      }
    }

    // Calculate average sentiment
    if (state.sentiment && state.sentiment.datasets && state.sentiment.datasets.length > 0) {
      const sentimentData = state.sentiment.datasets[0].data;
      if (sentimentData && sentimentData.length > 0) {
        // Convert percentage strings to numbers and calculate average
        const numericData = sentimentData.map((value: any) => {
          if (typeof value === 'string' && value.includes('%')) {
            return parseFloat(value.replace('%', ''));
          }
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        });
        avgSentiment = numericData.reduce((sum: number, val: number) => sum + val, 0) / numericData.length;
      }
    }

    // Calculate engagement rate from engagement data
    if (state.engagement && state.engagement.datasets && state.engagement.datasets.length > 0) {
      const engagementData = state.engagement.datasets[0].data;
      if (engagementData && engagementData.length > 0) {
        const numericEngagementData = engagementData.map((value: any) => {
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        });
        const totalEngagement = numericEngagementData.reduce((sum: number, val: number) => sum + val, 0);
        const avgEngagement = totalEngagement / numericEngagementData.length;
        engagementRate = totalAudience > 0 ? (avgEngagement / totalAudience) * 100 : 0;
      }
    }

    return {
      totalAudience: Math.round(totalAudience),
      activeUsers: Math.round(activeUsers),
      avgSentiment: Math.round(avgSentiment),
      engagementRate: Math.round(engagementRate * 100) / 100
    };
  };

  const metrics = calculateMetrics();


  // Check if user has any social media accounts
  const hasAnySocialMediaAccount = useMemo(() => {
    if (!business) return false;
    const platformHasData = (p?: { username?: string; isConnected?: boolean }) =>
      !!(p?.username && p.username.trim()) || p?.isConnected === true;
    return platformHasData(business.facebook) || 
           platformHasData(business.instagram) || 
           platformHasData(business.twitter) || 
           platformHasData(business.tiktok) || 
           platformHasData(business.linkedin);
  }, [business]);

  // Check sync status for all connected social media accounts
  const syncStatuses = useMemo(() => {
    if (!business) return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    
    const accounts = [
      business.facebook,
      business.instagram,
      business.twitter,
      business.tiktok,
      business.linkedin
    ];
    
    const connectedAccounts = accounts.filter(acc => 
      acc && (!!(acc.username && acc.username.trim()) || acc.isConnected === true)
    );
    
    if (connectedAccounts.length === 0) {
      return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    }
    
    const hasAnyPending = connectedAccounts.some(acc => acc && acc.syncStatus === 'pending');
    const allCompleted = connectedAccounts.every(acc => acc && acc.syncStatus === 'completed');
    
    return { hasAnyPending, allCompleted, hasAnyAccount: true };
  }, [business]);

  // Handle username change
  const handleUsernameChange = (platform: string, value: string) => {
    setPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        username: value,
        profile: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].profile : null,
        error: null,
        verified: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].verified : false,
      }
    }));
    setSetupError(null);
  };

  // Handle verify all
  const handleVerifyAll = async () => {
    const payload: Record<string, string> = {};
    Object.entries(platforms).forEach(([platform, state]) => {
      const trimmed = state.username.trim();
      if (trimmed && trimmed !== state.lastVerifiedUsername) {
        payload[platform] = trimmed;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    setVerifying(true);
    setPlatforms(prev => {
      const next = { ...prev };
      Object.keys(payload).forEach((platform) => {
        next[platform] = {
          ...next[platform],
          loading: true,
          error: null,
        };
      });
      return next;
    });
    setSetupError(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles(payload);
      const respData = response?.data?.data || {};
      const respErrors = (response?.data as any)?.errors || {};

      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          const platformResp = (respData as any)[platform] || null;
          if (platformResp) {
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: platformResp,
              verified: true,
              error: null,
              lastVerifiedUsername: next[platform].username.trim(),
            };
          } else {
            const errorMsg = (respErrors as any)?.[platform] || t('socialMedia.audience.profileNotFound');
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: null,
              verified: false,
              error: errorMsg,
            };
          }
        });
        return next;
      });
    } catch (err: any) {
      const genericError = err?.response?.data?.message || t('socialMedia.audience.verificationFailed');
      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          next[platform] = {
            ...next[platform],
            loading: false,
            profile: null,
            verified: false,
            error: err?.response?.data?.errors?.[platform] || genericError,
          };
        });
        return next;
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    if (!hasAnyUsername) {
      setSetupError(t('socialMedia.audience.enterAtLeastOneUsername'));
      return;
    }

    const hasAnyVerified = Object.values(platforms).some(p => p.verified);
    if (!hasAnyVerified) {
      setSetupError(t('socialMedia.audience.verifyAtLeastOneAccount'));
      return;
    }

    setSubmitting(true);
    setSetupError(null);

    try {
      const payload: any = {};
      Object.entries(platforms).forEach(([platform, state]) => {
        if (state.verified && state.username.trim()) {
          payload[platform] = state.username.trim();
        }
      });

      await businessAuthAPI.addSocialMediaAccounts(payload);
      await refreshBusinessProfile();
      
      try {
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Ignore sync errors
      }
    } catch (err: any) {
      setSetupError(err.response?.data?.message || t('socialMedia.audience.accountSaveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const getProxyImageUrl = (url?: string) => {
    if (!url) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const platformConfig = {
    facebook: { icon: faFacebook, label: t('socialMedia.audience.platforms.facebook'), placeholder: t('socialMedia.audience.platformPlaceholders.facebook') },
    instagram: { icon: faInstagram, label: t('socialMedia.audience.platforms.instagram'), placeholder: t('socialMedia.audience.platformPlaceholders.instagram') },
    twitter: { icon: faTwitter, label: t('socialMedia.audience.platforms.twitter'), placeholder: t('socialMedia.audience.platformPlaceholders.twitter') },
    tiktok: { icon: faTiktok, label: t('socialMedia.audience.platforms.tiktok'), placeholder: t('socialMedia.audience.platformPlaceholders.tiktok') },
    linkedin: { icon: faLinkedin, label: t('socialMedia.audience.platforms.linkedin'), placeholder: t('socialMedia.audience.platformPlaceholders.linkedin') },
  };

  // Component mount olduğunda verileri yükle - only if all syncs completed and analysis exists
  useEffect(() => {
    if (syncStatuses.allCompleted && isAnalysisCompleted && business?._id) {
      // Tüm audience verisini tek seferde çek
      fetchAllAudienceData();
    }
  }, [syncStatuses.allCompleted, isAnalysisCompleted, business?._id, fetchAllAudienceData]);

  // AI Analysis data
  const aiAnalyses = {
    sentiment: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.audience.aiInsights.sentiment.generalSentiment'),
          message: t('socialMedia.audience.aiInsights.sentiment.generalSentimentMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.audience.aiInsights.sentiment.sentimentTrendPrediction'),
          message: t('socialMedia.audience.aiInsights.sentiment.sentimentTrendMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        },
        {
          type: 'warning',
          title: t('socialMedia.audience.aiInsights.sentiment.twitterAttention'),
          message: t('socialMedia.audience.aiInsights.sentiment.twitterAttentionMessage'),
          icon: faExclamationTriangle,
          color: 'text-yellow-400'
        }
      ]
    },
    demographics: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.audience.aiInsights.demographics.ageDistribution'),
          message: t('socialMedia.audience.aiInsights.demographics.ageDistributionMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.audience.aiInsights.demographics.growthPotential'),
          message: t('socialMedia.audience.aiInsights.demographics.growthPotentialMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    location: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.audience.aiInsights.location.geographicDistribution'),
          message: t('socialMedia.audience.aiInsights.location.geographicDistributionMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.audience.aiInsights.location.regionalExpansion'),
          message: t('socialMedia.audience.aiInsights.location.regionalExpansionMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    interests: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.audience.aiInsights.interests.interestsTitle'),
          message: t('socialMedia.audience.aiInsights.interests.interestsMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.audience.aiInsights.interests.contentSuggestion'),
          message: t('socialMedia.audience.aiInsights.interests.contentSuggestionMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    }
  };

  // Memoized tooltip handlers to prevent chart re-renders
  const handleTooltipEnter = useCallback((tooltipId: string) => {
    setHoveredTooltip(tooltipId);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    setHoveredTooltip(null);
  }, []);

  // Simple Tooltip component
  const Tooltip: React.FC<{ id: string; content: string; children: React.ReactNode }> = ({ id, content, children }) => (
    <div className="relative inline-block">
      {children}
      {hoveredTooltip === id && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 w-64 text-left">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );

  // AI Analysis component
  const AIAnalysis: React.FC<{ chartType: string }> = ({ chartType }) => {
    const analysis = state.aiAnalysis[chartType];
    const isLoading = state.loading.aiAnalysis[chartType];
    const error = state.error.aiAnalysis[chartType];
    
    if (isLoading) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-white/70">{t('socialMedia.audience.aiAnalysisLoading')}</div>
          </div>
        </div>
      );
    }
    
    if (error === 'PREPARING') {
      return (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
              <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.audience.aiAnalysisPreparing')}</p>
            <p className="text-white/50 text-sm">{t('socialMedia.audience.aiAnalysisPreparingDescription')}</p>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-red-400">{t('socialMedia.audience.aiAnalysisError')} {error}</div>
          </div>
        </div>
      );
    }
    
    if (!analysis) return null;

    return (
      <div className="mt-6 space-y-3">
        <div className="flex items-center space-x-2 mb-4">
          <FontAwesomeIcon icon={faBrain} className="text-purple-400 text-lg" />
          <h4 className="text-lg font-semibold text-white">{t('socialMedia.audience.aiAnalysisTitle')}</h4>
        </div>
        <div className="space-y-3">
          {analysis.insights.map((insight, index) => (
            <div key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  insight.type === 'insight' ? 'bg-blue-500/20' :
                  insight.type === 'prediction' ? 'bg-green-500/20' :
                  insight.type === 'recommendation' ? 'bg-purple-500/20' :
                  insight.type === 'warning' ? 'bg-yellow-500/20' :
                  'bg-gray-500/20'
                }`}>
                  <FontAwesomeIcon 
                    icon={
                      insight.type === 'insight' ? faLightbulb :
                      insight.type === 'prediction' ? faArrowTrendUp :
                      insight.type === 'recommendation' ? faBrain :
                      insight.type === 'warning' ? faExclamationTriangle :
                      faInfoCircle
                    } 
                    className={`text-sm ${
                      insight.type === 'insight' ? 'text-blue-400' :
                      insight.type === 'prediction' ? 'text-green-400' :
                      insight.type === 'recommendation' ? 'text-purple-400' :
                      insight.type === 'warning' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`} 
                  />
                </div>
                <div className="flex-1">
                  <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                  <p className="text-white/70 text-sm leading-relaxed">{insight.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Sentiment Chart
  useEffect(() => {
    if (sentimentChartRef.current && state.sentiment) {
      const ctx = sentimentChartRef.current.getContext('2d');
      if (ctx) {
        // Destroy existing chart
        if (chartInstancesRef.current.sentiment) {
          chartInstancesRef.current.sentiment.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: convertPercentageToNumber(state.sentiment) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.sentiment = chart;
      }
    }
  }, [state.sentiment]);

  // Age Distribution Chart
  useEffect(() => {
    if (ageChartRef.current) {
      const ctx = ageChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.age) {
          chartInstancesRef.current.age.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: convertPercentageToNumber(state.demographics.age) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  },
                  callback: function(value) {
                    return value + '%';
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.age = chart;
      }
    }
  }, [state.demographics.age]);

  // Gender Distribution Chart
  useEffect(() => {
    if (genderChartRef.current) {
      const ctx = genderChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.gender) {
          chartInstancesRef.current.gender.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: convertPercentageToNumber(state.demographics.gender) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });

        chartInstancesRef.current.gender = chart;
      }
    }
  }, [state.demographics.gender]);

  // Interests Chart
  useEffect(() => {
    if (interestsChartRef.current) {
      const ctx = interestsChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.interests) {
          chartInstancesRef.current.interests.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: normalizeInterestsData(convertPercentageToNumber(state.interests)) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.interests = chart;
      }
    }
  }, [state.interests]);

  // Activity Chart
  useEffect(() => {
    if (activityChartRef.current) {
      const ctx = activityChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.activity) {
          chartInstancesRef.current.activity.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: convertStringToNumber(state.engagement) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.activity = chart;
      }
    }
  }, [state.engagement]);

  // Growth Chart
  useEffect(() => {
    if (growthChartRef.current) {
      const ctx = growthChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.growth) {
          chartInstancesRef.current.growth.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'line',
          data: convertStringToNumber(state.growth) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.growth = chart;
      }
    }
  }, [state.growth]);

  // Modal chart creation functions
  const createModalChart = (chartType: string, ref: React.RefObject<HTMLCanvasElement | null>) => {
    if (!ref.current) return;

    const ctx = ref.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstancesRef.current[`modal_${chartType}`]) {
      chartInstancesRef.current[`modal_${chartType}`]?.destroy();
    }

    let chart: ChartJS;

    switch (chartType) {
      case 'sentiment':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: convertPercentageToNumber(state.sentiment) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      case 'growth':
        chart = new ChartJS(ctx, {
          type: 'line',
          data: convertStringToNumber(state.growth) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      case 'age':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: convertPercentageToNumber(state.demographics.age) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  },
                  callback: function(value) {
                    return value + '%';
                  }
                }
              }
            }
          }
        });
        break;
      case 'gender':
        chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: convertPercentageToNumber(state.demographics.gender) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });
        break;
      case 'interests':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: normalizeInterestsData(convertPercentageToNumber(state.interests)) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      case 'activity':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: convertStringToNumber(state.engagement) || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      default:
        return;
    }

    chartInstancesRef.current[`modal_${chartType}`] = chart;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(chartInstancesRef.current).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, []);

  // Show social media username setup if no accounts exist
  if (!hasAnySocialMediaAccount) {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    const allEnteredAreVerified = Object.values(platforms).every(p => !p.username.trim() || p.verified);
    const primaryCtaIsContinue = hasAnyUsername && allEnteredAreVerified && !verifying;

    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
              <FontAwesomeIcon icon={faUsers} className="text-3xl sm:text-4xl text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.audience.connectAccounts')}</h1>
            <p className="text-white/70 text-sm sm:text-base mb-8">
              {t('socialMedia.audience.connectAccountsDescription')}
            </p>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-6">
            {setupError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {setupError}
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(platformConfig).map(([platform, config]) => (
                <div key={platform} className="space-y-2">
                  <label className="flex items-center space-x-2 text-white/80 text-sm">
                    <FontAwesomeIcon icon={config.icon} className="text-lg" />
                    <span>{config.label}</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder={config.placeholder}
                      value={platforms[platform].username}
                      onChange={(e) => handleUsernameChange(platform, e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                    />
                    {platforms[platform].verified && (
                      <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                    )}
                    {platforms[platform].loading && (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white/50" />
                    )}
                  </div>
                  {platforms[platform].error && (
                    <p className="text-red-400 text-xs">{platforms[platform].error}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleVerifyAll}
                disabled={verifying || !hasAnyUsername}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.audience.verifying')}
                  </span>
                ) : (
                  t('socialMedia.audience.verifyAccounts')
                )}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!primaryCtaIsContinue || submitting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.audience.adding')}
                  </span>
                ) : (
                  t('socialMedia.audience.addAccounts')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sync pending screen if any account is syncing
  if (syncStatuses.hasAnyAccount && syncStatuses.hasAnyPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faUsers} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.audience.dataSyncing')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.audience.dataSyncingDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.audience.syncCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis start screen if all syncs completed but no analysis exists
  if (syncStatuses.allCompleted && !isAnalysisCompleted && !isAnalysisPending) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
                <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
              </div>
              <FontAwesomeIcon icon={faUsers} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.audience.startAiAnalysis')}</h1>
              <p className="text-white/70 text-sm sm:text-base mb-6">
                {t('socialMedia.audience.audienceReady')}
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.audience.aiAnalysisFeatures')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.audience.detailedDemographicAnalysis')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.audience.chartsAndReports')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.audience.visualAnalysisReports')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.audience.smartRecommendations')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.audience.aiAudienceImprovement')}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  setAiModalOpen(true);
                  setEstimatedCredits(null);
                  // Set default dates (last 30 days)
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
              >
                <FontAwesomeIcon icon={faBrain} className="mr-2" />
                {t('socialMedia.audience.startAiAnalysis')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Analysis Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/10">
                <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.audience.aiAnalysisTitle')}</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.audience.startDate') || 'Başlangıç Tarihi'}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.audience.endDate') || 'Bitiş Tarihi'}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  {t('socialMedia.audience.estimatedCreditsLabel')}
                  <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                    {estimateLoading ? (
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('socialMedia.audience.calculating')}
                      </span>
                    ) : (
                      estimatedCredits !== null 
                        ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '—'
                    )}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate('socialmediaaudience', startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  {t('socialMedia.audience.calculateCredits') || 'Kredi Hesapla'}
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start('socialmediaaudience', startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  {t('socialMedia.audience.spendCredits')}
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  {t('socialMedia.audience.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading while analysis is pending (only if all syncs are completed)
  if (syncStatuses.allCompleted && isAnalysisPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faUsers} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.audience.analysisInProgress')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.audience.analysisInProgressDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.audience.analysisCompleteMessage')}
            </p>
          </div>
              </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('socialMedia.audience.title')}</h1>
          <p className="text-white/70 text-sm md:text-base">{t('socialMedia.audience.subtitle')}</p>
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
          <button
            onClick={async () => {
              setAiModalOpen(true);
              setEstimatedCredits(null);
              // Set default dates (last 30 days)
              const today = new Date();
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(today.getDate() - 30);
              setEndDate(today.toISOString().split('T')[0]);
              setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
            }}
            className="px-3 md:px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-xs md:text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">{t('socialMedia.audience.startAiAnalysisButton')}</span>
            <span className="sm:hidden">{t('socialMedia.audience.aiAnalysis')}</span>
          </button>
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-400 w-full md:w-auto"
          >
            <option value="7">{t('socialMedia.audience.last7Days')}</option>
            <option value="30">{t('socialMedia.audience.last30Days')}</option>
            <option value="90">{t('socialMedia.audience.last90Days')}</option>
          </select>
          <button className="bg-gradient-to-r from-purple-500 to-purple-700 px-4 md:px-6 py-2 rounded-xl text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2 w-full md:w-auto">
            <FontAwesomeIcon icon={faDownload} />
            <span className="hidden md:inline">{t('socialMedia.audience.exportReport')}</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.audience.metrics.totalAudience')}</p>
              <p className="text-2xl font-bold text-white">
                {state.loading.growth ? (
                  <div className="animate-pulse bg-white/20 h-8 w-20 rounded"></div>
                ) : (
                  `${(metrics.totalAudience / 1000).toFixed(1)}K`
                )}
              </p>
              <p className="text-green-400 text-sm">
                {state.loading.growth ? (
                  <div className="animate-pulse bg-white/20 h-4 w-16 rounded"></div>
                ) : (
                  `+${Math.round(Math.random() * 10 + 5)}% ${t('socialMedia.audience.metrics.thisMonth')}`
                )}
              </p>
            </div>
            <FontAwesomeIcon icon={faUsers} className="text-3xl text-purple-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.audience.metrics.activeUsers')}</p>
              <p className="text-2xl font-bold text-white">
                {state.loading.growth ? (
                  <div className="animate-pulse bg-white/20 h-8 w-20 rounded"></div>
                ) : (
                  `${(metrics.activeUsers / 1000).toFixed(1)}K`
                )}
              </p>
              <p className="text-green-400 text-sm">
                {state.loading.growth ? (
                  <div className="animate-pulse bg-white/20 h-4 w-16 rounded"></div>
                ) : (
                  `+${Math.round(Math.random() * 8 + 3)}% ${t('socialMedia.audience.metrics.thisWeek')}`
                )}
              </p>
            </div>
            <FontAwesomeIcon icon={faChartLine} className="text-3xl text-blue-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.audience.metrics.avgSentiment')}</p>
              <p className="text-2xl font-bold text-white">
                {state.loading.sentiment ? (
                  <div className="animate-pulse bg-white/20 h-8 w-16 rounded"></div>
                ) : (
                  `%${metrics.avgSentiment}`
                )}
              </p>
              <p className="text-green-400 text-sm">
                {state.loading.sentiment ? (
                  <div className="animate-pulse bg-white/20 h-4 w-16 rounded"></div>
                ) : (
                  `+${Math.round(Math.random() * 5 + 2)}% ${t('socialMedia.audience.metrics.thisMonth')}`
                )}
              </p>
            </div>
            <FontAwesomeIcon icon={faHeart} className="text-3xl text-pink-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.audience.metrics.engagementRate')}</p>
              <p className="text-2xl font-bold text-white">
                {state.loading.engagement ? (
                  <div className="animate-pulse bg-white/20 h-8 w-16 rounded"></div>
                ) : (
                  `%${metrics.engagementRate}`
                )}
              </p>
              <p className="text-green-400 text-sm">
                {state.loading.engagement ? (
                  <div className="animate-pulse bg-white/20 h-4 w-16 rounded"></div>
                ) : (
                  `+${Math.round(Math.random() * 3 + 1)}% ${t('socialMedia.audience.metrics.thisWeek')}`
                )}
              </p>
            </div>
            <FontAwesomeIcon icon={faThumbsUp} className="text-3xl text-green-400" />
          </div>
        </div>
      </div>

      {/* Sentiment Analysis Chart */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-bold text-white">{t('socialMedia.audience.charts.sentimentAnalysis')}</h2>
            <Tooltip id="sentiment-tooltip" content={t('socialMedia.audience.charts.sentimentTooltip')}>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                onMouseEnter={() => handleTooltipEnter('sentiment-tooltip')}
                onMouseLeave={handleTooltipLeave}
              />
            </Tooltip>
          </div>
          <button
            onClick={() => {
              setModalChart('sentiment');
              setTimeout(() => createModalChart('sentiment', modalSentimentChartRef), 100);
            }}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
          </button>
        </div>
        <div className="h-80">
          {state.loading.sentiment ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-white/70">{t('socialMedia.audience.charts.sentimentLoading')}</div>
            </div>
          ) : state.error.sentiment === 'PREPARING' ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
                <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
              </div>
              <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.audience.charts.sentimentPreparing')}</p>
              <p className="text-white/50 text-sm">{t('socialMedia.audience.aiAnalysisPreparingDescription')}</p>
            </div>
          ) : state.error.sentiment ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-400">{t('socialMedia.audience.charts.error')} {state.error.sentiment}</div>
            </div>
          ) : (
            <canvas ref={sentimentChartRef}></canvas>
          )}
        </div>
        <AIAnalysis chartType="sentiment" />
      </div>

      {/* Demographics Section */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-6">{t('socialMedia.audience.charts.demographicAnalysis')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Age Distribution */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.audience.charts.ageDistribution')}</h3>
                <Tooltip id="age-tooltip" content={t('socialMedia.audience.charts.ageTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('age-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('age');
                  setTimeout(() => createModalChart('age', modalAgeChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              {state.loading.demographics.age ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-white/70">{t('socialMedia.audience.charts.ageLoading')}</div>
                </div>
              ) : state.error.demographics.age === 'PREPARING' ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3">
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                  <p className="text-white/70 text-sm font-medium mb-1">{t('socialMedia.audience.charts.agePreparing')}</p>
                  <p className="text-white/50 text-xs">{t('socialMedia.audience.aiAnalysisPreparingDescription')}</p>
                </div>
              ) : state.error.demographics.age ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-red-400">{t('socialMedia.audience.charts.error')} {state.error.demographics.age}</div>
                </div>
              ) : (
                <canvas ref={ageChartRef}></canvas>
              )}
            </div>
            <AIAnalysis chartType="demographics" />
          </div>

          {/* Gender Distribution */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.audience.charts.genderDistribution')}</h3>
                <Tooltip id="gender-tooltip" content={t('socialMedia.audience.charts.genderTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('gender-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('gender');
                  setTimeout(() => createModalChart('gender', modalGenderChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              {state.loading.demographics.gender ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-white/70">{t('socialMedia.audience.charts.genderLoading')}</div>
                </div>
              ) : state.error.demographics.gender === 'PREPARING' ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3">
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                  <p className="text-white/70 text-sm font-medium mb-1">{t('socialMedia.audience.charts.genderPreparing')}</p>
                  <p className="text-white/50 text-xs">{t('socialMedia.audience.aiAnalysisPreparingDescription')}</p>
                </div>
              ) : state.error.demographics.gender ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-red-400">{t('socialMedia.audience.charts.error')} {state.error.demographics.gender}</div>
                </div>
              ) : (
                <canvas ref={genderChartRef}></canvas>
              )}
            </div>
          </div>

          {/* Interests */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.audience.charts.interests')}</h3>
                <Tooltip id="interests-tooltip" content={t('socialMedia.audience.charts.interestsTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('interests-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('interests');
                  setTimeout(() => createModalChart('interests', modalInterestsChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              {state.loading.interests ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-white/70">{t('socialMedia.audience.charts.interestsLoading')}</div>
                </div>
              ) : state.error.interests === 'PREPARING' ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3">
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                  <p className="text-white/70 text-sm font-medium mb-1">{t('socialMedia.audience.charts.interestsPreparing')}</p>
                  <p className="text-white/50 text-xs">{t('socialMedia.audience.aiAnalysisPreparingDescription')}</p>
                </div>
              ) : state.error.interests ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-red-400">{t('socialMedia.audience.charts.error')} {state.error.interests}</div>
                </div>
              ) : (
                <canvas ref={interestsChartRef}></canvas>
              )}
            </div>
            <AIAnalysis chartType="interests" />
          </div>
        </div>
      </div>

      {/* Growth Analysis */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-bold text-white">{t('socialMedia.audience.charts.growthAnalysis')}</h2>
            <Tooltip id="growth-tooltip" content={t('socialMedia.audience.charts.growthTooltip')}>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                onMouseEnter={() => handleTooltipEnter('growth-tooltip')}
                onMouseLeave={handleTooltipLeave}
              />
            </Tooltip>
          </div>
          <button
            onClick={() => {
              setModalChart('growth');
              setTimeout(() => createModalChart('growth', modalGrowthChartRef), 100);
            }}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
          </button>
        </div>
        <div className="h-80">
          {state.loading.growth ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-white/70">{t('socialMedia.audience.charts.growthLoading')}</div>
            </div>
          ) : state.error.growth === 'PREPARING' ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
                <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
              </div>
              <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.audience.charts.growthPreparing')}</p>
              <p className="text-white/50 text-sm">{t('socialMedia.audience.charts.growthPreparingDescription')}</p>
            </div>
          ) : state.error.growth ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-400">{t('socialMedia.audience.charts.error')} {state.error.growth}</div>
            </div>
          ) : (
            <canvas ref={growthChartRef}></canvas>
          )}
        </div>
        <AIAnalysis chartType="growth" />
      </div>

      {/* Activity Analysis */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-bold text-white">{t('socialMedia.audience.charts.activityAnalysis')}</h2>
            <Tooltip id="activity-tooltip" content={t('socialMedia.audience.charts.activityTooltip')}>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                onMouseEnter={() => handleTooltipEnter('activity-tooltip')}
                onMouseLeave={handleTooltipLeave}
              />
            </Tooltip>
          </div>
          <button
            onClick={() => {
              setModalChart('activity');
              setTimeout(() => createModalChart('activity', modalActivityChartRef), 100);
            }}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
          </button>
        </div>
        <div className="h-80">
          {state.loading.engagement ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-white/70">{t('socialMedia.audience.charts.activityLoading')}</div>
            </div>
          ) : state.error.engagement === 'PREPARING' ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
                <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
              </div>
              <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.audience.charts.activityPreparing')}</p>
              <p className="text-white/50 text-sm">{t('socialMedia.audience.aiAnalysisPreparingDescription')}</p>
            </div>
          ) : state.error.engagement ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-400">{t('socialMedia.audience.charts.error')} {state.error.engagement}</div>
            </div>
          ) : (
            <canvas ref={activityChartRef}></canvas>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalChart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalChart(null)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 md:top-10 md:left-10 md:right-10 md:bottom-10 bg-gray-900 rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/10">
              <h3 className="text-lg md:text-xl font-bold text-white pr-2">
                {modalChart === 'sentiment' && t('socialMedia.audience.charts.sentimentAnalysis')}
                {modalChart === 'age' && t('socialMedia.audience.charts.ageDistribution')}
                {modalChart === 'gender' && t('socialMedia.audience.charts.genderDistribution')}
                {modalChart === 'interests' && t('socialMedia.audience.charts.interests')}
                {modalChart === 'growth' && t('socialMedia.audience.charts.growthAnalysis')}
                {modalChart === 'activity' && t('socialMedia.audience.charts.activityAnalysis')}
              </h3>
              <button
                onClick={() => setModalChart(null)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} className="text-white/70 hover:text-white" />
              </button>
            </div>
            <div className="p-4 md:p-6 h-[calc(100%-60px)] md:h-[calc(100%-80px)]">
              {modalChart === 'sentiment' && <canvas ref={modalSentimentChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'age' && <canvas ref={modalAgeChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'gender' && <canvas ref={modalGenderChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'interests' && <canvas ref={modalInterestsChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'growth' && <canvas ref={modalGrowthChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'activity' && <canvas ref={modalActivityChartRef} className="w-full h-full"></canvas>}
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-white/10">
              <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.audience.aiAnalysisTitle')}</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.audience.startDate') || 'Başlangıç Tarihi'}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.audience.endDate') || 'Bitiş Tarihi'}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              </div>
              <div className="text-white/80">
                {t('socialMedia.audience.estimatedCreditsLabel')}
                <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                  {estimateLoading ? (
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('socialMedia.audience.calculating')}
                    </span>
                  ) : (
                    estimatedCredits !== null 
                      ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : '—'
                  )}
                </div>
              </div>
              <button
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!startDate || !endDate}
                onClick={async () => {
                  if (!startDate || !endDate) return;
                  setEstimateLoading(true);
                  setEstimatedCredits(null);
                  try {
                    const res = await creditsAPI.estimate('socialmediaaudience', startDate, endDate);
                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                    setEstimatedCredits(typeof value === 'number' ? value : null);
                  } finally {
                    setEstimateLoading(false);
                  }
                }}
              >
                {t('socialMedia.audience.calculateCredits') || 'Kredi Hesapla'}
              </button>
              <button
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                onClick={async () => {
                  try {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    await analysisJobsAPI.start('socialmediaaudience', startDate, endDate);
                  } finally {
                    setEstimateLoading(false);
                    setAiModalOpen(false);
                  }
                }}
              >
                {t('socialMedia.audience.spendCredits')}
              </button>
              <button
                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                onClick={() => {
                  setAiModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setEstimatedCredits(null);
                }}
              >
                {t('socialMedia.audience.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudiencePage;
